import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

type HeightMode = 'auto' | 'tall' | 'full';

interface MobileBottomSheetProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    heightMode?: HeightMode;
    showCloseButton?: boolean;
}

export function MobileBottomSheet({
    open,
    onClose,
    title,
    children,
    heightMode = 'tall',
    showCloseButton = true,
}: MobileBottomSheetProps) {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const dragStartY = useRef<number | null>(null);
    const sheetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            setMounted(true);
            const frame = requestAnimationFrame(() => setVisible(true));
            return () => cancelAnimationFrame(frame);
        }
        setVisible(false);
        const timeout = setTimeout(() => setMounted(false), 220);
        return () => clearTimeout(timeout);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [open, onClose]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        dragStartY.current = e.touches[0].clientY;
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (dragStartY.current === null) return;
        const delta = e.touches[0].clientY - dragStartY.current;
        if (delta > 0) setDragOffset(delta);
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (dragOffset > 80) {
            onClose();
        }
        setDragOffset(0);
        dragStartY.current = null;
    }, [dragOffset, onClose]);

    if (!mounted) return null;

    const heightClass = {
        auto: 'max-h-[85vh]',
        tall: 'h-[85vh]',
        full: 'h-[100dvh]',
    }[heightMode];

    return createPortal(
        <div
            className="fixed inset-0 z-[60] flex flex-col justify-end"
            aria-modal="true"
            role="dialog"
        >
            {/* Backdrop */}
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className={cn(
                    'absolute inset-0 bg-black/60 transition-opacity duration-200',
                    visible ? 'opacity-100' : 'opacity-0'
                )}
            />

            {/* Sheet */}
            <div
                ref={sheetRef}
                className={cn(
                    'relative bg-background rounded-t-2xl shadow-2xl',
                    'flex flex-col overflow-hidden',
                    'transition-transform duration-200 ease-out will-change-transform',
                    heightClass,
                    visible ? 'translate-y-0' : 'translate-y-full'
                )}
                style={{
                    transform:
                        dragOffset > 0 && visible
                            ? `translateY(${dragOffset}px)`
                            : undefined,
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                }}
            >
                {/* Drag handle */}
                <div
                    className="pt-3 pb-2 flex justify-center flex-shrink-0 cursor-grab active:cursor-grabbing"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="w-10 h-1 rounded-full bg-muted-foreground/40" />
                </div>

                {/* Header */}
                {(title || showCloseButton) && (
                    <div className="flex items-center justify-between px-4 pb-3 border-b border-border flex-shrink-0">
                        <h2 className="text-lg font-semibold text-foreground">
                            {title}
                        </h2>
                        {showCloseButton && (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground active:bg-accent"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
            </div>
        </div>,
        document.body
    );
}
